/**
 * useAiChat — logica del floating AI chat bubble pubblico.
 * ★ Round 2 qualità — Extract Hook da AIChatBubble.jsx: session id, teaser
 * proattivo, invio messaggi a /api/public/chat. Comportamento INVARIATO.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';

const STORAGE_SESSION = 'gp_ai_chat_session';
const STORAGE_OPENED_ONCE = 'gp_ai_chat_opened';
const STORAGE_DISMISSED = 'gp_ai_chat_dismissed';
const PROACTIVE_DELAY_MS = 60000; // 60 seconds
const PROACTIVE_BUBBLE_HIDE_MS = 12000; // hide the proactive teaser after 12s if not clicked

export const PROACTIVE_GREETINGS = [
  'Ciao 👋 Posso aiutarti? Sono l\'assistente di Richard, posso rispondere su Lombok, La Mar, Chumphon o Narea.',
  'Hai una domanda specifica sull\'acquisto? Te la giro subito.',
  'Vuoi capire il piano pagamenti a rate mensili? Te lo spiego in 30 secondi.',
];

export const SUGGESTED_QUESTIONS = [
  'Quanto serve per iniziare?',
  'Come funziona il piano pagamenti?',
  'Quando posso visitare un resort?',
  'Quali rendimenti posso aspettarmi?',
];

const generateSessionId = () => `gp-public-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function useAiChat({ enabled }) {
  const [open, setOpen] = useState(false);
  const [proactiveVisible, setProactiveVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const greetingRef = useRef(PROACTIVE_GREETINGS[Math.floor(Math.random() * PROACTIVE_GREETINGS.length)]);

  // Restore or create a stable session id (so the AI keeps context within this visit)
  useEffect(() => {
    try {
      let sid = sessionStorage.getItem(STORAGE_SESSION);
      if (!sid) {
        sid = generateSessionId();
        sessionStorage.setItem(STORAGE_SESSION, sid);
      }
      sessionIdRef.current = sid;
    } catch (err) {
      logger.debug('AIChatBubble sessionStorage read failed', err);
      sessionIdRef.current = generateSessionId();
    }
  }, []);

  // Schedule proactive teaser (only once, only if user hasn't dismissed)
  useEffect(() => {
    if (!enabled) return undefined;
    let dismissed = false;
    let openedOnce = false;
    try {
      dismissed = localStorage.getItem(STORAGE_DISMISSED) === '1';
      openedOnce = localStorage.getItem(STORAGE_OPENED_ONCE) === '1';
    } catch (err) {
      logger.debug('AIChatBubble: localStorage read failed', err);
    }
    if (dismissed || openedOnce) return undefined;

    const t = setTimeout(() => setProactiveVisible(true), PROACTIVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled]);

  // Auto-hide the teaser after some time (the floating button stays visible)
  useEffect(() => {
    if (!proactiveVisible) return undefined;
    const t = setTimeout(() => setProactiveVisible(false), PROACTIVE_BUBBLE_HIDE_MS);
    return () => clearTimeout(t);
  }, [proactiveVisible]);

  // Auto-scroll the message list
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  // Generate a stable monotonic id for each message so React reconciliation
  // is correct even when the list grows during streaming.
  const messageIdRef = useRef(0);
  const newMessageId = useCallback(() => {
    messageIdRef.current += 1;
    return `m-${messageIdRef.current}`;
  }, []);

  const handleOpenChat = () => {
    setOpen(true);
    setProactiveVisible(false);
    try { localStorage.setItem(STORAGE_OPENED_ONCE, '1'); } catch (err) { logger.warn('AIChatBubble localStorage:', err); }
    // Seed an assistant greeting if empty
    if (messages.length === 0) {
      setMessages([
        {
          id: newMessageId(),
          role: 'assistant',
          content: greetingRef.current,
        },
      ]);
    }
  };

  const handleClose = () => setOpen(false);

  const handleDismissProactive = (e) => {
    e?.stopPropagation?.();
    setProactiveVisible(false);
    try { localStorage.setItem(STORAGE_DISMISSED, '1'); } catch (err) { logger.warn('AIChatBubble localStorage:', err); }
  };

  const sendMessage = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setInput('');
    const newMessages = [...messages, { id: newMessageId(), role: 'user', content: value }];
    setMessages(newMessages);
    setSending(true);
    try {
      const r = await api.post('/public/chat', {
        message: value,
        session_id: sessionIdRef.current,
        history: messages.map((m) => ({ role: m.role, content: m.content })),
        page_context: typeof window !== 'undefined' ? window.location.pathname : null,
      });
      const reply =
        r?.data?.reply ||
        r?.data?.response ||
        r?.data?.text ||
        'Ho avuto un piccolo intoppo, riprova tra qualche secondo.';
      setMessages([...newMessages, { id: newMessageId(), role: 'assistant', content: reply }]);
    } catch (err) {
      logger.error('AIChatBubble: send failed', err);
      const fallback =
        'Sto avendo un problema tecnico. Nel frattempo, scrivici direttamente a greenparadise@housecashflows.com — Richard ti risponderà personalmente.';
      setMessages([...newMessages, { id: newMessageId(), role: 'assistant', content: fallback }]);
    } finally {
      setSending(false);
    }
  };

  return {
    open,
    proactiveVisible,
    messages,
    input,
    setInput,
    sending,
    greeting: greetingRef.current,
    messagesEndRef,
    handleOpenChat,
    handleClose,
    handleDismissProactive,
    sendMessage,
  };
}
